import type { AcademicYear, Term } from "@/types/api";

/**
 * The term facts the academic-year popover puts on screen.
 *
 * The popover shows each term's dates, its posting status and the submission
 * deadline that `terms.gradeSubmissionDeadline` already stores and nothing in
 * the app reads. All of it is derived here rather than in the component so the
 * date handling is testable on its own.
 */

/** Badge variants used for term status, matching @gradebook/ui's Badge. */
export type TermStatusVariant = "success" | "secondary" | "info" | "warning";

export interface TermStatusDescriptor {
  label: string;
  variant: TermStatusVariant;
}

/**
 * A date-only string (`2026-08-24`) parses as UTC midnight. Formatting that in
 * the viewer's own timezone renders the previous day for everyone west of
 * Greenwich, so every date here is both parsed and formatted in UTC.
 */
function parseISODate(value: string | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value.length <= 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(date.getTime()) ? null : date;
}

const dayMonth = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});

const dayMonthYear = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

/** `2026-08-24` → `24 Aug`. Empty string when there is no date to show. */
export function formatDayMonth(value: string | undefined): string {
  const date = parseISODate(value);
  return date ? dayMonth.format(date) : "";
}

/** `2026-12-18` → `18 Dec 2026`. Empty string when there is no date to show. */
export function formatDayMonthYear(value: string | undefined): string {
  const date = parseISODate(value);
  return date ? dayMonthYear.format(date) : "";
}

/** The `24 Aug – 12 Dec` line under a term name. */
export function formatTermRange(term: Term): string {
  const start = formatDayMonth(term.startDate);
  const end = formatDayMonth(term.endDate);

  if (start && end) return `${start} – ${end}`;
  if (start) return `from ${start}`;
  if (end) return `until ${end}`;
  return "no dates";
}

/** `Term 1 grades due 18 Dec 2026`, or null for a term with no deadline set. */
export function formatSubmissionDeadline(term: Term): string | null {
  const due = formatDayMonthYear(term.gradeSubmissionDeadline);
  return due ? `${term.termName} grades due ${due}` : null;
}

/**
 * The one deadline worth putting on the popover: the next one still to come.
 *
 * Taking the first term that happens to carry a deadline would keep advertising
 * "Term 1 grades due 18 Dec" all through the summer term. Terms arrive in
 * teaching order, so the first deadline on or after today is the next one due;
 * once the year's deadlines have all passed the last of them is shown, because
 * "nothing due" and "you missed it" are not the same message.
 */
export function formatNextSubmissionDeadline(
  terms: Term[],
  now: Date = new Date()
): string | null {
  const dated = terms.filter((term) => parseISODate(term.gradeSubmissionDeadline));
  if (dated.length === 0) return null;

  // A date-only deadline parses to that day's UTC midnight, so a plain `>= now`
  // would call the deadline past from one minute into the day it falls on. The
  // whole of the due day still counts as upcoming.
  const DAY_MS = 24 * 60 * 60 * 1000;
  const upcoming = dated.find((term) => {
    const due = parseISODate(term.gradeSubmissionDeadline);
    return due !== null && due.getTime() + DAY_MS > now.getTime();
  });

  return formatSubmissionDeadline(upcoming ?? dated[dated.length - 1]);
}

/**
 * How a term reads on the badge.
 *
 * "Upcoming" is not a stored status — a term is upcoming when it is still open
 * and its start date has not arrived, which is what separates next term from
 * the one being taught right now.
 */
export function termStatus(term: Term, now: Date = new Date()): TermStatusDescriptor {
  switch (term.status) {
    case "open": {
      const start = parseISODate(term.startDate);
      if (start && start.getTime() > now.getTime()) {
        return { label: "Upcoming", variant: "secondary" };
      }
      return { label: "Open", variant: "success" };
    }
    case "submitted":
      return { label: "Submitted", variant: "info" };
    case "in_review":
      return { label: "In review", variant: "info" };
    case "approved":
      return { label: "Approved", variant: "info" };
    case "locked":
      return { label: "Locked", variant: "warning" };
    default: {
      // Unreachable for today's `TermStatus`, and the `never` binding keeps it
      // that way: adding a status to the union breaks this line first. It is
      // still here because the shell renders on every page, so a status a
      // deployed bundle predates must degrade to a plain badge rather than
      // return undefined and take the whole app to the error boundary.
      const unmodelled: never = term.status;
      return { label: String(unmodelled), variant: "secondary" };
    }
  }
}

/**
 * The terms belonging to one academic year, in teaching order.
 *
 * Matches a term by id *or* — for rows that predate `academic_year_id` and
 * carry only the year's name — by name with a null id. This mirrors what
 * `GET /api/terms?academicYearId=` does server-side; grouping the unfiltered
 * list any other way would silently drop those legacy terms.
 */
export function termsForYear(terms: Term[], year: AcademicYear): Term[] {
  return terms
    .filter((term) => belongsToYear(term, year))
    .sort((a, b) => {
      const aStart = parseISODate(a.startDate);
      const bStart = parseISODate(b.startDate);
      // Undated terms have no place in the sequence, so they sit at the end
      // rather than pretending to be the first term of the year.
      if (!aStart && !bStart) return 0;
      if (!aStart) return 1;
      if (!bStart) return -1;
      return aStart.getTime() - bStart.getTime();
    });
}

/**
 * Which year a term belongs to — the inverse of `termsForYear`.
 *
 * A term id already implies its year, which is what lets a link that names a
 * term open in the year it was written in rather than in whichever year the
 * reader happens to be sitting in. Without this the gradebook's `?term=` would
 * resolve against the reader's own year, find nothing, and quietly fall back to
 * that year's first term — the link would open, on the wrong class.
 *
 * Null when no year claims it: a term from another school, or one whose year has
 * since been deleted. The caller has to be able to tell that from "some other
 * year", or it would scope the app to a year that is not there.
 */
export function yearForTerm(years: AcademicYear[], term: Term): AcademicYear | null {
  return years.find((year) => belongsToYear(term, year)) ?? null;
}

/**
 * Shared by both directions on purpose. Terms predate `academic_year_id`, so
 * some rows are joined to their year only by the `academic_year` name string,
 * and `GET /api/terms?academicYearId=` matches those too. Written once because
 * two copies of this rule drifting apart means a term that is in a year's list
 * but has no year of its own.
 */
function belongsToYear(term: Term, year: AcademicYear): boolean {
  return (
    term.academicYearId === year.id ||
    (!term.academicYearId && term.academicYear === year.name)
  );
}
